"use client";

import { createSlice, nanoid, type PayloadAction } from '@reduxjs/toolkit';
import type { CartLine, PosSaleState } from '@/types/pos/sale';
import { computePosTotals, initialPosTotals, initialPosState } from '@/types/pos/sale';
import { generateIdempotencyKey } from '@/services/utils/idempotency';

const posSaleSlice = createSlice({
  name: 'posSale',
  initialState: initialPosState,
  reducers: {
    // Controla la visibilidad del diálogo de venta rápida POS.
    openDialog(state) {
      state.isDialogOpen = true;
    },
    closeDialog(state) {
      state.isDialogOpen = false;
    },
    openCashSessionDialog(state) {
      state.isOpenCashSessionDialog = true;
    },
    closeCashSessionDialog(state) {
      state.isOpenCashSessionDialog = false;
    },
    setTerminal(state, action: PayloadAction<{ id: number | null; code?: string | null; name?: string | null }>) {
      state.terminalId = action.payload.id;
      state.terminalCode = action.payload.code ?? null;
      state.terminalName = action.payload.name ?? null;
      state.terminalStatus = 'loaded';
    },
    setTerminalStatus(state, action: PayloadAction<'idle' | 'loading' | 'loaded' | 'error'>) {
      state.terminalStatus = action.payload;
    },
    setCashSession(
      state,
      action: PayloadAction<{
        id: number | null;
        status?: string | null;
        openingAmount?: number | null;
        isStale?: boolean;
        canClose?: boolean;
        blockReason?: string | null;
        openedByCurrentUser?: boolean;
        terminalName?: string | null;
        openedByUsername?: string | null;
        openedByName?: string | null;
        businessDate?: string | null;
        businessTimezone?: string | null;
        closeoutHourSnapshot?: number | null;
        openedAt?: string | null;
        closedAt?: string | null;
      }>,
    ) {
      state.cashSessionId = action.payload.id;
      state.cashSessionStatus = action.payload.status ?? null;
      state.cashSessionOpeningAmount = action.payload.openingAmount ?? state.cashSessionOpeningAmount ?? null;
      state.cashSessionIsStale = action.payload.isStale ?? false;
      state.cashSessionCanClose = action.payload.canClose ?? false;
      state.cashSessionBlockReason = action.payload.blockReason ?? null;
      state.cashSessionOpenedByCurrentUser = action.payload.openedByCurrentUser ?? false;
      state.cashSessionTerminalName = action.payload.terminalName ?? null;
      state.cashSessionOpenedByUsername = action.payload.openedByUsername ?? null;
      state.cashSessionOpenedByName = action.payload.openedByName ?? null;
      state.cashSessionBusinessDate = action.payload.businessDate ?? null;
      state.cashSessionBusinessTimezone = action.payload.businessTimezone ?? null;
      state.cashSessionCloseoutHourSnapshot = action.payload.closeoutHourSnapshot ?? null;
      state.cashSessionOpenedAt = action.payload.openedAt ?? null;
      state.cashSessionClosedAt = action.payload.closedAt ?? null;
    },
    clearCashSession(state) {
      state.cashSessionId = null;
      state.cashSessionStatus = null;
      state.cashSessionOpeningAmount = null;
      state.cashSessionIsStale = false;
      state.cashSessionCanClose = false;
      state.cashSessionBlockReason = null;
      state.cashSessionOpenedByCurrentUser = false;
      state.cashSessionTerminalName = null;
      state.cashSessionOpenedByUsername = null;
      state.cashSessionOpenedByName = null;
      state.cashSessionBusinessDate = null;
      state.cashSessionBusinessTimezone = null;
      state.cashSessionCloseoutHourSnapshot = null;
      state.cashSessionOpenedAt = null;
      state.cashSessionClosedAt = null;
    },
    setUser(state, action: PayloadAction<number | null>) {
      state.userId = action.payload;
    },
    setCurrency(state, action: PayloadAction<{ currency: string; exchangeRate?: number }>) {
      state.currency = action.payload.currency;
      if (action.payload.exchangeRate != null) {
        state.exchangeRate = action.payload.exchangeRate;
      }
    },
    regenerateIdempotencyKey(state) {
      state.idempotencyKey = generateIdempotencyKey('pos-sale');
    },
    addLine(state, action: PayloadAction<Omit<CartLine, 'id'>>) {
      const payload = action.payload;
      const existing = state.cart.find((line) => {
        if (payload.inventory_id != null && line.inventory_id === payload.inventory_id) {
          return true;
        }
        if (payload.inventory_id == null && payload.sku && line.sku === payload.sku) {
          return true;
        }
        return false;
      });

      if (existing) {
        existing.qty += payload.qty;
        existing.unit_price = payload.unit_price;
        if (payload.available != null) {
          existing.available = payload.available;
        }
        if (payload.discount != null) {
          existing.discount = payload.discount;
        }
        if (payload.tax_rate != null) {
          existing.tax_rate = payload.tax_rate;
        }
      } else {
        const line: CartLine = { id: nanoid(), ...payload };
        state.cart.unshift(line);
      }

      state.totals = computePosTotals(state.cart);
    },
    updateLine(state, action: PayloadAction<{ id: string; patch: Partial<CartLine> }>) {
      state.cart = state.cart.map((line) =>
        line.id === action.payload.id ? { ...line, ...action.payload.patch } : line,
      );
      state.totals = computePosTotals(state.cart);
    },
    removeLine(state, action: PayloadAction<string>) {
      state.cart = state.cart.filter((line) => line.id !== action.payload);
      state.totals = computePosTotals(state.cart);
    },
    clearCart(state) {
      state.cart = [];
      state.totals = initialPosTotals;
    },
    setCustomer(state, action: PayloadAction<number | null>) {
      state.customerId = action.payload;
    },
    setNotes(state, action: PayloadAction<string>) {
      state.notes = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setLastSaleId(state, action: PayloadAction<number | null>) {
      state.lastSaleId = action.payload;
    },
    setLastSaleContext(
      state,
      action: PayloadAction<{
        saleId: number;
        savedAt: string;
        terminalId: number | null;
        companyId: number | null;
        branchId: number | null;
      } | null>,
    ) {
      if (!action.payload) {
        state.lastSaleId = null;
        state.lastSaleSavedAt = null;
        state.lastSaleTerminalId = null;
        state.lastSaleCompanyId = null;
        state.lastSaleBranchId = null;
        return;
      }
      state.lastSaleId = action.payload.saleId;
      state.lastSaleSavedAt = action.payload.savedAt;
      state.lastSaleTerminalId = action.payload.terminalId;
      state.lastSaleCompanyId = action.payload.companyId;
      state.lastSaleBranchId = action.payload.branchId;
    },
    resetSale(state) {
      state.cart = [];
      state.totals = initialPosTotals;
      state.customerId = null;
      state.notes = '';
      state.lastSaleId = null;
      state.lastSaleSavedAt = null;
      state.lastSaleTerminalId = null;
      state.lastSaleCompanyId = null;
      state.lastSaleBranchId = null;
      state.error = null;
      state.idempotencyKey = generateIdempotencyKey('pos-sale');
      state.terminalId = null;
      state.terminalCode = null;
      state.terminalName = null;
      state.terminalStatus = 'idle';
    },
    resetPosSaleState: () => ({
      ...initialPosState,
      totals: { ...initialPosTotals },
      cart: [],
    }),
  },
  extraReducers: (builder) => {
    builder.addCase('authentication/resetSession', () => ({
      ...initialPosState,
      totals: { ...initialPosTotals },
      cart: [],
    }));
  },
});

export const {
  openDialog,
  closeDialog,
  openCashSessionDialog,
  closeCashSessionDialog,
  setTerminal,
  setTerminalStatus,
  setCashSession,
  clearCashSession,
  setUser,
  setCurrency,
  regenerateIdempotencyKey,
  addLine,
  updateLine,
  removeLine,
  clearCart,
  setCustomer,
  setNotes,
  setError,
  setLastSaleId,
  setLastSaleContext,
  resetSale,
  resetPosSaleState,
} = posSaleSlice.actions;

export default posSaleSlice.reducer;
